<script setup lang="ts">
// Vue
import { computed, onMounted, ref } from "vue";

// Dependencies
import { isAxiosError } from "axios";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";

// Features
import Overlay from "@/features/Overlay";
import StepIntro from "../StepIntro/StepIntro.vue";
import {
  useCreateBuildingProjectWizard,
  useCreateBuildingProject,
  useModels,
  services as BuildingProjectServices,
} from "@/features/BuildingProject";

// Components
import Button from "@/components/Reusables/Buttons/Button.vue";
import LoadingLogoSpinner from "@/components/Reusables/Loading/LoadingLogoSpinner.vue";
import SpeckleConnectionChoices from "./SpeckleConnectionChoices.vue";
import ExistingSpeckleStream from "./WithSpeckleConnection/ExistingSpeckleStream.vue";
import NewSpeckleStream from "./WithSpeckleConnection/NewSpeckleStream.vue";
import WithoutModel from "./WithoutModel.vue";

const createBuildingProjectWizard = useCreateBuildingProjectWizard();
const models = useModels();
const createBuildingProject = useCreateBuildingProject();

const isCreatingProject = ref<boolean>(false);
const speckleConnectionChoice = ref<number | null>(createBuildingProjectWizard.getSpeckleConnectionChoice() ?? null);
const speckleStreamId = ref<string>();

/**
 * Whether the project this step is configuring already exists.
 *
 * It does when the start dialog hosts the flow: creating happens on leaving the building's areas, so
 * that this step has something to attach a stream TO. It does not on /create-project, whose bar is a
 * plain Next — there, this step is still the one that writes the project.
 *
 * Only the wording depends on it. The action is the same call either way, and the service decides which
 * half of the work is left to do (CreateBuildingProjectService).
 */
const projectAlreadyExists = computed(() => Boolean(createBuildingProject.getBuildingProjectId()));

const finishLabel = computed(() => {
  if (!projectAlreadyExists.value) return "Create project";

  // Only the two choices that actually attach something get the model wording. Before a choice is made
  // the button is disabled anyway, and naming an action the user has not picked would presume it.
  const attachesAModel = speckleConnectionChoice.value === 1 || speckleConnectionChoice.value === 2;
  return attachesAModel ? "Connect model" : "Finish";
});

// What still blocks creation for the chosen path — null when the step is ready to finish. Choice 0
// deliberately needs nothing. Choice 1 builds the new connection's models from the active selection
// (CreateBuildingProjectService.getDefaultBranches), so an empty selection would create a connection
// with no models in it. Choice 2 shares another project's stream, so one has to be picked. The
// message doubles as the caption beside the create button, so the button is never dimmed without
// the reason being readable; the null choice stays silent — the choice cards are that affordance.
const creationBlocker = computed<string | null>(() => {
  if (speckleConnectionChoice.value === 1 && models.getActiveModels().length === 0) {
    return "Select at least one model to create the connection with.";
  }
  if (speckleConnectionChoice.value === 2 && !speckleStreamId.value) {
    return "Choose the project whose model connection to share.";
  }
  return null;
});
const canCreate = computed(() => speckleConnectionChoice.value !== null && creationBlocker.value === null);

// The service returns the axios error it caught; the API's rejection message lives at
// response.data.message when the request reached it. Anything shaped differently gets the fallback.
const getCreateErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const message: unknown = error.response?.data?.message;
    if (typeof message === "string" && message.length > 0) return message;
  }
  return projectAlreadyExists.value
    ? "Could not connect the model. Please try again."
    : "Could not create the project. Please try again.";
};

const submitValidate = async () => {
  // The explicit null check narrows the choice to `number` for the service call — canCreate covers
  // it too, but through a computed TypeScript cannot see into.
  if (speckleConnectionChoice.value === null || !canCreate.value) return;

  // Start loader
  isCreatingProject.value = true;

  // Create project
  const response = await BuildingProjectServices.createBuildingProject({
    speckleConnectionChoice: speckleConnectionChoice.value,
    speckleStreamId: speckleStreamId.value,
  });

  // Stop loader
  isCreatingProject.value = false;

  if (response === 200) {
    createBuildingProjectWizard.handleStepForward();
  } else {
    Sentry.captureException(response);
    useToast().error(getCreateErrorMessage(response));
  }
};

const handleStepBack = () => {
  if (speckleConnectionChoice.value !== null) {
    handleResetChoice();
  } else {
    createBuildingProjectWizard.handleStepBack();
  }
};

const handleChoiceChosen = (id: number) => {
  speckleConnectionChoice.value = id;
  createBuildingProjectWizard.setSpeckleConnectionChoice(speckleConnectionChoice.value);
};

const handleResetChoice = () => {
  speckleConnectionChoice.value = null;
  speckleStreamId.value = undefined;
  createBuildingProjectWizard.setSpeckleConnectionChoice(speckleConnectionChoice.value);
};

const handleSpeckleIdChange = (speckleId?: string) => {
  speckleStreamId.value = speckleId;
};

onMounted(() => {
  models.setDefaultModels();
});
</script>

<template>
  <StepIntro
    heading="How will the model reach this project?"
    hint="Start a new Speckle connection, use one that already exists, or continue without a 3D model." />

  <!-- Content section -->
  <SpeckleConnectionChoices
    v-if="speckleConnectionChoice === null"
    @chosen="handleChoiceChosen" />
  <WithoutModel v-if="speckleConnectionChoice === 0" />
  <NewSpeckleStream v-if="speckleConnectionChoice === 1" />
  <ExistingSpeckleStream
    v-if="speckleConnectionChoice === 2"
    @submit="handleSpeckleIdChange" />
  <!-- Content section END -->

  <!-- The step's actions live in the pinned bar's slots like every other step's, teleported so the
       state stays here: Back in the way-out slot (it resets the choice first — finding 32), and the
       finishing Create always in the forward slot — this is the last step, so the finish line stays
       visible, dimmed until the chosen path is ready (canCreate), with the blocker named beside it.
       `defer`, because this step mounts before the bar. -->
  <Teleport
    defer
    to="#wizard-bar-custom-left">
    <Button
      label="Back"
      color="button"
      @click="handleStepBack" />
  </Teleport>
  <Teleport
    defer
    to="#wizard-bar-custom-right">
    <div class="gap-px-12 flex items-center">
      <Transition name="fade-in-quick">
        <span
          v-if="creationBlocker"
          class="t-caption text-type-muted"
          >{{ creationBlocker }}</span
        >
      </Transition>
      <!-- Really disabled, not opacity-faked: the fake kept an enabled button whose washed-out text
           failed the contrast gate, and it stayed keyboard-activatable. The caption above carries the
           why — a tooltip can't, since a natively disabled button fires no mouse events. -->
      <Button
        :label="finishLabel"
        :color="canCreate ? 'primary' : 'disabled'"
        :disabled="!canCreate"
        data-cy="create-project-btn"
        @click="submitValidate" />
    </div>
  </Teleport>

  <!-- Project is creating -->
  <Overlay :show="() => isCreatingProject">
    <div class="rounded-16 bg-slate-0 flex h-fit w-[400px] flex-col items-center justify-center gap-12 p-8">
      <LoadingLogoSpinner class="h-24 w-24" />

      <!-- Two jobs, two messages: on /create-project this step still writes the project, while in the
           start dialog it is only connecting a model to one that already exists. -->
      <div class="flex flex-col gap-2 text-center">
        <span class="t-small-bold text-type-default">{{
          projectAlreadyExists ? "Connecting your model" : "Creating your project"
        }}</span>
        <span class="t-small text-type-muted">{{
          projectAlreadyExists
            ? "Setting up the connection and linking it to your project. This usually takes a few seconds."
            : "Setting up the calculation and saving your answers. This usually takes a few seconds."
        }}</span>
      </div>
    </div>
  </Overlay>
</template>
